import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../../config/constants';
import axios from 'axios';

const RegistrationSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        // Get session_id and pendingRegistrationId from URL query params
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        const pendingRegistrationId = params.get('pending_id');
        
        if (!sessionId && !pendingRegistrationId) {
          setError('Missing session information. Please try registering again.');
          setLoading(false);
          return;
        }
        
        // If we have a pendingRegistrationId, check its status
        if (pendingRegistrationId) {
          const response = await axios.get(`${API_URL}/registration/status/${pendingRegistrationId}`);
          
          if (response.data.success) {
            setRegistrationData(response.data);
            setLoading(false);
          } else {
            setError('Unable to verify registration status. Please contact support.');
            setLoading(false);
          }
        } else {
          // Just show success message if we only have session_id
          setRegistrationData({ status: 'pending' });
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking registration status:', err);
        setError('An error occurred while verifying your registration. Please contact support.');
        setLoading(false);
      }
    };
    
    checkRegistrationStatus();
  }, [location.search]);
  
  const handleLoginClick = () => {
    navigate('/login');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <svg className="animate-spin h-12 w-12 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Verifying your registration...
          </h2>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 mb-6">
            <div className="flex items-center mb-4">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Registration Error</h3>
            </div>
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={() => navigate('/signup')}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Floating orbs */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-primary-500/10 dark:bg-primary-400/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-primary-400/15 dark:bg-primary-300/15 rounded-full blur-lg animate-pulse animation-delay-1000"></div>
        <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-primary-600/10 dark:bg-primary-500/10 rounded-full blur-2xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-20 right-20 w-28 h-28 bg-primary-500/15 dark:bg-primary-400/15 rounded-full blur-xl animate-pulse animation-delay-3000"></div>
      </div>
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary-400/30 dark:bg-primary-300/30 rounded-full animate-bounce animation-delay-1000"></div>
        <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-primary-500/40 dark:bg-primary-400/40 rounded-full animate-bounce animation-delay-2000"></div>
        <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-primary-600/30 dark:bg-primary-500/30 rounded-full animate-bounce animation-delay-3000"></div>
        <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-primary-400/40 dark:bg-primary-300/40 rounded-full animate-bounce animation-delay-4000"></div>
      </div>
      
      {/* Main content */}
      <div className="relative z-10 w-full max-w-2xl">
        {/* Main card */}
        <div className="bg-white dark:bg-gray-800 backdrop-blur-lg border border-gray-200 dark:border-gray-700 rounded-3xl p-8 md:p-12 shadow-2xl text-center">
          {/* Celebration Header */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full mb-6 animate-bounce">
              <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <span className="text-2xl animate-ping">🎉</span>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                  Welcome to LeaveWise!
                </h1>
                <span className="text-2xl animate-ping animation-delay-500">🎉</span>
              </div>
              <p className="text-xl text-gray-600 dark:text-gray-300 font-medium">
                Your subscription is active and ready to go!
              </p>
            </div>
          </div>
          
          {/* Status-specific content */}
          {registrationData?.status === 'completed' ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 animate-bounce">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-green-800 dark:text-green-200">🚀 Account Ready!</h3>
              </div>
              <p className="text-green-700 dark:text-green-300 text-lg">
                Your LeaveWise account has been created successfully!
              </p>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200">🚀 Account Ready!</h3>
              </div>
              <p className="text-blue-700 dark:text-blue-300 text-lg">
                Your payment was successful! We're setting up your account.
              </p>
            </div>
          )}
          
          {/* CTA Button */}
          <div className="mb-8">
            <button
              onClick={handleLoginClick}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-lg rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-500/50 shadow-lg hover:shadow-xl"
            >
              <svg className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Login to Continue
              <svg className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
          
          {/* Support Footer */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-600">
            <p className="text-gray-500 dark:text-gray-400 mb-2">
              Questions? We're here to help! 💬
            </p>
            <a 
              href="mailto:support@leavewise.app" 
              className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              support@leavewise.app
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
